import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { useIdentityAuth } from '@auth/IdentityAuthContext';
import { useTranslation } from 'react-i18next';
import AppRoutes from '@routes/AppRoutes';
import { ThemeProvider } from '@contexts/ThemeContext';
import { StoreProvider, useStore } from '@contexts/StoreContext';
import { UserAccessProvider, useUserAccess } from '@contexts/UserAccessContext';
import ThemeManager from '@components/ThemeManager';
import Login from '@components/Login';
import AuthCallback from '@auth/AuthCallback';
import SilentCallback from '@auth/SilentCallback';
import LogoutCallback from '@auth/LogoutCallback';
import styles from '@styles/App.module.css';
import { ChannelsProvider } from './contexts/ChannelsContext';
import { GeneralHoursProvider } from './contexts/GeneralHoursContext';
import { OrdersProvider } from './contexts/OrdersContext';
import { AuditLogsProvider } from './contexts/AuditLogsContext';
import { SoundProvider } from '@contexts/SoundContext';
import OrderSoundNotifier from '@components/OrderSoundNotifier';

import Loading from './components/common/Loading';
import Error from './components/common/Error';
import AuthorizationError from './components/common/AuthorizationError';
import { OrgConfigProvider, useOrgConfig } from '@contexts/OrgConfigContext';
import { VersionProvider } from '@contexts/VersionContext';
import { useFavicon } from '@hooks/useFavicon';

const router = createBrowserRouter(AppRoutes);

const FaviconBinder: React.FC = () => {
  const { branding } = useOrgConfig();
  useFavicon(branding?.faviconUrl ?? null);
  return null;
};

function AuthenticatedApp() {
  const { basicData, basicLoading, basicError, data, restaurantId } =
    useStore();
  const { t } = useTranslation();
  const { logout } = useIdentityAuth();
  const { hasSmpOrgGrant, hasSmpEnabledOrg, allStores, selectedStore } =
    useUserAccess();

  const hasNoStores = !basicLoading && allStores.length === 0;

  if (basicLoading) {
    return <Loading message={t('loading.storeData')} />;
  }

  if (!hasSmpOrgGrant) {
    return (
      <Error
        title={t('error.noAccessTitle')}
        message={t('error.noAccessMessage')}
        showLogout
        onLogout={logout}
      />
    );
  }

  if (!hasSmpEnabledOrg) {
    return (
      <Error
        title={t('error.smpNotEnabledTitle')}
        message={t('error.smpNotEnabledMessage')}
        showLogout
        onLogout={logout}
      />
    );
  }

  if (hasNoStores) {
    return (
      <Error
        title={t('error.noStoreTitle')}
        message={t('error.noStoreMessage')}
        showLogout
        onLogout={logout}
      />
    );
  }

  if (basicError) {
    return (
      <Error
        message={`${t('loading.storeData')}: ${basicError}`}
        showLogout
        onLogout={logout}
      />
    );
  }

  const appTree = (
    <OrgConfigProvider
      orgId={selectedStore?.organisationId ?? null}
      marketId={basicData?.marketId ?? null}
    >
      <FaviconBinder />
      <ChannelsProvider store={data} restaurantId={restaurantId}>
        <GeneralHoursProvider restaurantId={restaurantId}>
          <OrdersProvider store={data} restaurantId={restaurantId}>
            <AuditLogsProvider restaurantId={restaurantId}>
              <ThemeManager />
              <SoundProvider>
                <OrderSoundNotifier />
                <VersionProvider>
                  <RouterProvider router={router} />
                </VersionProvider>
              </SoundProvider>
            </AuditLogsProvider>
          </OrdersProvider>
        </GeneralHoursProvider>
      </ChannelsProvider>
    </OrgConfigProvider>
  );

  return appTree;
}

function App() {
  const {
    isLoading,
    isAuthenticated,
    isAuthorized,
    authorizationError,
    logout,
  } = useIdentityAuth();
  const { t } = useTranslation();

  if (window.location.pathname.startsWith('/auth/callback')) {
    return <AuthCallback />;
  }

  if (window.location.pathname.startsWith('/auth/silent-redirect')) {
    return <SilentCallback />;
  }

  if (window.location.pathname.startsWith('/auth/logout')) {
    return <LogoutCallback />;
  }

  return (
    <ThemeProvider>
      <div className={styles.app}>
        {isLoading || isAuthorized === null ? (
          <Loading message={t('loading.authentication')} />
        ) : (
          <Suspense fallback={<Loading message={t('loading.application')} />}>
            {isAuthenticated ? (
              isAuthorized ? (
                <UserAccessProvider>
                  <StoreProvider>
                    <AuthenticatedApp />
                  </StoreProvider>
                </UserAccessProvider>
              ) : (
                <AuthorizationError
                  message={
                    authorizationError ||
                    'You do not have access to this application. Verify your permissions and contact your support team.'
                  }
                  onLogout={logout}
                />
              )
            ) : (
              <Login />
            )}
          </Suspense>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
